import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, SkeletonRows } from "../components/ui/Feedback";
import { SearchField, SelectField } from "../components/ui/Input";
import { Pill } from "../components/ui/Status";
import { Cell, Pagination, TableFrame, TableHead, TableRow } from "../components/ui/Table";
import type { Column } from "../components/ui/Table";
import { Metric, MicroLabel } from "../components/ui/Text";
import { ApiError, getPage } from "../lib/api";
import { changedFields, entryShape, targetTypesOf } from "../lib/audit";
import type { FieldChange } from "../lib/audit";
import { count, since } from "../lib/format";
import type { StatusTone } from "../lib/status";
import { AUDIT_OUTCOMES, type AuditEntry, type AuditOutcome } from "../lib/types";

/**
 * The audit trail. Spec 9.
 *
 * ## What this page is for
 *
 * One question, asked after something has already gone wrong: *who did this, and
 * what did it look like before?* Every mutating route has recorded the answer
 * since spec 9 and nothing could read it back, so until now it was answered by
 * whoever held the database credential — the same person the trail is evidence
 * against.
 *
 * ## Why the diff is the row's content and not a sentence
 *
 * The server sends the two state blobs exactly as they were recorded and
 * deliberately does not summarise them. An entry's value is that it is *the state
 * the route saw*; a phrase like "changed the price" is an interpretation, and one
 * written today about a row written last year. Expanding a row shows the fields
 * that moved, with the recorded values on both sides, and nothing else.
 *
 * ## Why so few filters
 *
 * The vocabulary is the server's — action names, target types, outcomes — and the
 * dropdowns are built from the entries on screen rather than from a fixed list,
 * for `MessageTail`'s reason. Actor and target are not form fields: both are ids
 * nobody types, so they arrive by clicking the row that shows them, as an
 * identity filter does on the sessions page.
 *
 * There is no date range yet. The trail is newest-first and pages backwards, the
 * server accepts `from`/`to` already, and a picker here has to answer "in whose
 * timezone" — `usageWindow.ts`'s question — which is worth doing properly rather
 * than quickly.
 */

const COLUMNS: readonly Column[] = [
  { key: "when", label: "When", width: "10%" },
  { key: "actor", label: "Actor", width: "20%" },
  { key: "action", label: "Action", width: "20%" },
  { key: "target", label: "Target" },
  { key: "outcome", label: "Outcome", width: "10%" },
];

/** How an outcome reads at a glance. A refusal is the one worth a colour. */
const OUTCOME_TONES: Readonly<Record<AuditOutcome, StatusTone>> = {
  ok: "idle",
  denied: "faulted",
  failed: "faulted",
};

export function Audit() {
  const [params, setParams] = useSearchParams();
  const [expanded, setExpanded] = useState<string | undefined>(undefined);

  const action = params.get("action") ?? "";
  const targetType = params.get("targetType") ?? "";
  const outcome = params.get("outcome") ?? "";
  /**
   * Not form fields: both are ids, and an operator arrives at them by clicking the
   * actor or the target on a row rather than by typing a uuid. Shown as something
   * to clear instead of something to fill in — the sessions page's treatment of
   * `identityId`.
   */
  const actorId = params.get("actorId") ?? "";
  const targetId = params.get("targetId") ?? "";
  const page = positiveInt(params.get("page")) ?? 1;

  const entries = useQuery({
    queryKey: ["audit", { action, targetType, targetId, actorId, outcome, page }],
    queryFn: () =>
      getPage<AuditEntry>("/audit", {
        query: { action, targetType, targetId, actorId, outcome, page },
      }),
    placeholderData: (previous) => previous,
  });

  /**
   * Refusals, counted whatever the table is filtered to. It belongs in the
   * masthead for the reason the events page keeps its warning count there: it is
   * the number somebody opened this page to find, and a filtered table cannot
   * report it.
   */
  const denied = useQuery({
    queryKey: ["audit", "denied-count"],
    queryFn: () => getPage<AuditEntry>("/audit", { query: { outcome: "denied", limit: 1 } }),
  });

  const rows = entries.data?.rows ?? [];
  const isFiltered = [action, targetType, targetId, actorId, outcome].some((one) => one !== "");

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  /**
   * A 403 is not an error to retry, it is an answer: this account does not hold
   * `admin`. It gets a sentence saying so instead of the generic notice, which
   * would offer a Retry button for a request that will never succeed.
   */
  const forbidden = entries.error instanceof ApiError && entries.error.isForbidden;

  return (
    <>
      <PageHeader
        eyebrow="Audit"
        title="Audit log"
        metrics={
          <>
            <Metric
              label="Entries"
              value={count(entries.data?.meta?.total)}
              hint="matching filters"
            />
            <Metric
              label="Refused"
              value={count(denied.data?.meta?.total)}
              hint="all time"
              tone={(denied.data?.meta?.total ?? 0) > 0 ? "signal" : "muted"}
            />
          </>
        }
        lede="Every change an operator or a service token has made, as the route recorded it. Append-only: nothing here can be edited or deleted from the panel, and a refused attempt is kept beside the ones that succeeded."
      />

      {forbidden ? (
        <EmptyState
          title="The audit log needs the View the audit log permission."
          detail="This list names each operator, their address, and the before and after of decisions they may not have been part of — so it is granted on its own rather than with the other views. Ask an administrator to give you a role that includes it."
        />
      ) : (
        <>
          <form
            onSubmit={(event) => event.preventDefault()}
            className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 lg:grid-cols-4"
          >
            <SelectField
              label="Action"
              value={action}
              onChange={(event) => setFilter("action", event.target.value)}
              hint="What the route called it"
            >
              <option value="">Any</option>
              {optionsFrom(
                rows.map((row) => row.action),
                action,
              ).map((one) => (
                <option key={one} value={one}>
                  {one}
                </option>
              ))}
            </SelectField>

            <SelectField
              label="Target type"
              value={targetType}
              onChange={(event) => setFilter("targetType", event.target.value)}
            >
              <option value="">Any</option>
              {optionsFrom(targetTypesOf(rows), targetType).map((one) => (
                <option key={one} value={one}>
                  {one}
                </option>
              ))}
            </SelectField>

            <SelectField
              label="Outcome"
              value={outcome}
              onChange={(event) => setFilter("outcome", event.target.value)}
            >
              <option value="">Any</option>
              {AUDIT_OUTCOMES.map((one) => (
                <option key={one} value={one}>
                  {one}
                </option>
              ))}
            </SelectField>

            {/*
              The one id worth typing: a station id is short, printed on the
              sticker, and pasted out of a ticket. Actor and target uuids are not,
              which is why they have no field of their own.
            */}
            <SearchField
              label="Target id"
              value={targetId}
              placeholder="CP-0001"
              hint="Exact match"
              onChange={(event) => setFilter("targetId", event.target.value)}
            />
          </form>

          {actorId === "" ? null : (
            <div className="flex items-baseline gap-3">
              <MicroLabel>One actor: {rows[0]?.actorLabel ?? actorId}</MicroLabel>
              <Button variant="ghost" onClick={() => setFilter("actorId", "")}>
                Show everyone
              </Button>
            </div>
          )}

          {entries.isError ? (
            <ErrorNotice error={entries.error} onRetry={() => void entries.refetch()} />
          ) : null}

          <div className="flex flex-col gap-3">
            <TableFrame>
              <TableHead columns={COLUMNS} />
              {entries.isPending ? (
                <SkeletonRows rows={8} columns={COLUMNS.length} />
              ) : (
                <tbody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={row.id}
                      index={index}
                      isFaulted={row.outcome !== "ok"}
                      onActivate={() => setExpanded(expanded === row.id ? undefined : row.id)}
                    >
                      <Cell muted>{since(row.occurredAt)}</Cell>
                      <Cell>
                        <div className="flex flex-col gap-0.5">
                          <button
                            type="button"
                            className="text-left font-medium text-ink underline decoration-rule underline-offset-2 hover:decoration-signal"
                            onClick={(event) => {
                              // The row's own handler expands it; filtering is a
                              // different intent and must not also toggle.
                              event.stopPropagation();
                              setFilter("actorId", row.actorId);
                            }}
                          >
                            {row.actorLabel}
                          </button>
                          <span className="text-micro tracking-normal normal-case text-ink-faint">
                            {row.actorType}
                            {row.actorIp === null ? "" : ` · ${row.actorIp}`}
                          </span>
                        </div>
                      </Cell>
                      <Cell>{row.action}</Cell>
                      <Cell>
                        <div className="flex flex-col gap-0.5">
                          <span className="break-all text-ink">{row.targetId}</span>
                          <span className="text-micro tracking-normal normal-case text-ink-faint">
                            {row.targetType}
                          </span>
                          {expanded === row.id ? <Changes entry={row} /> : null}
                        </div>
                      </Cell>
                      <Cell>
                        <Pill tone={OUTCOME_TONES[row.outcome]}>{row.outcome}</Pill>
                      </Cell>
                    </TableRow>
                  ))}
                </tbody>
              )}
            </TableFrame>

            {!entries.isPending && rows.length === 0 ? (
              <EmptyState
                title={
                  isFiltered ? "No entries match these filters." : "Nothing has been recorded yet."
                }
                detail="An entry is written whenever an operator or a service token changes something — a station registered, a card's allowance assigned, a price minted, a remote command sent — and whenever one of those is refused. Reads are not recorded: opening a page is not a change, and a trail that logged every list would bury the entries that matter."
              />
            ) : null}

            <Pagination
              meta={entries.data?.meta}
              shown={rows.length}
              onPage={(next) => {
                const nextParams = new URLSearchParams(params);
                if (next <= 1) nextParams.delete("page");
                else nextParams.set("page", String(next));
                setParams(nextParams);
              }}
            />
          </div>
        </>
      )}
    </>
  );
}

/**
 * What moved, field by field, with the recorded values on both sides.
 *
 * Field names are the server's own and are not prettified: reading a value here
 * and grepping the logs for it is the workflow this supports, and a display name
 * would break it. An em dash means the state did not carry that field at all,
 * which is a different fact from a recorded `null`.
 */
function Changes({ entry }: { readonly entry: AuditEntry }) {
  const shape = entryShape(entry);
  const fields = changedFields(entry.beforeState, entry.afterState);

  return (
    <div className="mt-2 flex flex-col gap-1.5 border-l-2 border-rule pl-3">
      <MicroLabel>{SHAPE_LABELS[shape]}</MicroLabel>
      {fields.length === 0 ? (
        <span className="text-micro tracking-normal normal-case text-ink-faint">
          No state was recorded with this entry.
        </span>
      ) : (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
          {fields.map((field) => (
            <div key={field.key} className="col-span-2 grid grid-cols-subgrid">
              <dt className="text-micro tracking-normal normal-case text-ink-faint">{field.key}</dt>
              <dd className="text-data break-all text-ink">
                <Movement field={field} />
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/**
 * `before → after`, with the old value struck through.
 *
 * Both sides, always, rather than only the new one: the question this page is
 * opened with is what something used to be.
 */
function Movement({ field }: { readonly field: FieldChange }) {
  return (
    <span className="flex flex-wrap items-baseline gap-2">
      <span className="text-ink-faint line-through">{field.before ?? "—"}</span>
      <span aria-hidden="true" className="text-ink-faint">
        →
      </span>
      <span>{field.after ?? "—"}</span>
    </span>
  );
}

const SHAPE_LABELS: Readonly<Record<ReturnType<typeof entryShape>, string>> = {
  created: "Created",
  deleted: "Deleted",
  changed: "Changed",
  recorded: "Recorded",
};

/**
 * The values seen on this page, plus whatever is selected — so a filter never
 * removes its own option from the list that set it.
 */
function optionsFrom(seen: readonly string[], selected: string): readonly string[] {
  const options = new Set(seen);
  if (selected !== "") options.add(selected);

  return [...options].sort();
}

function positiveInt(raw: string | null): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined;
  const value = Number(raw);
  return value >= 1 ? value : undefined;
}
